import { useCallback, useRef } from 'react';
import { EcommerceImpression, EcommerceProductDetails, EcommercePurchase } from 'ts/shared/interfaces/Ecommerce';
import { useLocation } from 'react-router-dom';

const useDataLayer = (isTour: boolean = false) => {
  const category = isTour ? 'TOURS OFFLINE' : 'SMPK';
  const location = useLocation();
  const ecommerceFlag = useRef(localStorage.getItem('__e_comerce'));
  const initDataLayer = useCallback(() => {
    if (typeof window !== 'undefined' && !window.dataLayer) {
      window.dataLayer = [];
    }
  }, []);

  const pushImpression = useCallback(
    (packages: any[]) => {
      if (!ecommerceFlag.current) {
        return;
      }
      const areImpressions = sessionStorage.getItem('packageImpressions' + location.search);
      if (areImpressions) {
        return;
      }
      initDataLayer();
      try {
        if (typeof window !== 'undefined' && window.dataLayer) {
          const impressions = packages.map((pkg, index) => ({
            id: pkg.dealId + '.' + pkg.rowId + '.' + pkg.hotelId,
            name: pkg.rowName,
            category: category,
            price: Number(pkg.displayPrice.toFixed(2)),
            brand: '',
            variant: 'הלוך חזור',
            list: 'תוצאות חיפוש',
            position: index,
            dimension1: 0,
            dimension2: pkg.dealLength,
            dimension3: '',
          }));

          window.dataLayer.push({
            event: 'impression',
            ecommerce: {
              impressions,
            },
          } as EcommerceImpression);

          sessionStorage.setItem('packageImpressions' + location.search, '1');
        }
      } catch (error) {
        console.error('Error pushing impression to dataLayer:', error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initDataLayer]
  );

  const pushPackageProductDetails = useCallback(
    (dealData: any) => {
      if (!ecommerceFlag.current) {
        return;
      }
      initDataLayer();
      try {
        if (typeof window === 'undefined' || !window.dataLayer) {
          return;
        }
        if (!dealData?.deal) {
          console.warn('Invalid package structure');
          return;
        }
        const { deal } = dealData;

        const dealId = deal.dealId || '';
        const rowId = deal.rowId || '';
        const hotelId = deal.hotelId || '';
        const id = `${dealId}.${rowId}.${hotelId}`;

        const name = deal.rowName || '';

        const packagePrice =
          deal.displayPrice || deal.multiPrice?.gross?.value || deal.salePrice || deal.price || 0;
        const price = Number(packagePrice.toFixed(2));

        const packageBrand = (deal.brand || category).replace(/'/g, '');

        const dealLength = parseInt(deal.dealLength || '0', 10);

        const data = {
          event: 'productDetails',
          ecommerce: {
            id: id,
            name: name,
            category: category,
            price: price,
            brand: packageBrand,
            variant: 'הלוך חזור',
            dimension1: 0,
            dimension2: dealLength,
            dimension3: '',
          },
        } as EcommerceProductDetails;
        window.dataLayer.push(data);
      } catch (error) {
        console.error('Error pushing package product details to dataLayer:', error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initDataLayer]
  );

  const pushPackagePurchase = useCallback(() => {
    if (!ecommerceFlag.current) {
      return;
    }
    initDataLayer();
    if (typeof window === 'undefined' || !window.dataLayer) {
      return;
    }

    try {
      const orderStr = sessionStorage.getItem('packageSuccessOrder');
      if (!orderStr) {
        console.warn('No packageSuccessOrder found in sessionStorage');
        return;
      }

      const order = JSON.parse(orderStr);
      const orderId = order?.orderId;
      const deal = order?.details?.deal;
      const orderSegment = order?.order?.order?.segments?.[0];

      if (!deal) {
        console.warn('Invalid package order structure');
        return;
      }

      const paymentOrderStr = sessionStorage.getItem('packagePaymentOrder') || '';
      const paymentOrderData = paymentOrderStr ? JSON.parse(paymentOrderStr) : undefined;
      const transactionId =
        order?.transactionId || paymentOrderData?.transactionId || (orderId ? `RQ${orderId}` : '');

      const trackingKey = `packagePurchaseTracked_${orderId || transactionId}`;

      if (sessionStorage.getItem(trackingKey)) {
        return;
      }

      const dealId = deal?.dealId || '';
      const rowId = deal?.rowId || '';
      const hotelId = deal?.hotelId || '';
      const id = `${dealId}.${rowId}.${hotelId}`;

      const name = deal?.dealTitle || deal?.rowName || '';

      const revenueRaw =
        order?.order?.order?.segments?.reduce(
          (acc: number, seg: any) => acc + (seg?.sysTotalGross2 ?? seg?.sysTotalGross ?? 0),
          0
        ) ||
        order?.total ||
        order?.totalPrice ||
        0;
      const revenue = Number(Number(revenueRaw).toFixed(2));

      const variant = 'הלוך חזור';

      const nights = Number.parseInt(deal?.dealLength || deal?.serviceLength || '0', 10) || 0;

      const suppPnr = orderSegment?.suppPnr || '';
      const suppPnrSeg = orderSegment?.suppPnrSeg || '';
      const dimension3 = `${suppPnr} ${suppPnrSeg}`.trim();

      const data = {
        event: 'purchase',
        ecommerce: {
          purchase: {
            actionField: {
              id: transactionId,
              revenue: revenue + '',
            },
            products: [
              {
                id,
                name: name,
                category: category,
                price: revenue,
                brand: category,
                variant: variant,
                dimension1: 0,
                dimension2: nights,
                dimension3: dimension3,
                quantity: 1,
                coupon: '',
              },
            ],
          },
        },
      } as EcommercePurchase;
      window.dataLayer.push(data);

      sessionStorage.setItem(trackingKey, 'true');
    } catch (error) {
      console.error('Error pushing package purchase to dataLayer:', error);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initDataLayer]);

  return {
    pushImpression,
    pushPackagePurchase,
    pushPackageProductDetails,
  };
};

export default useDataLayer;
